import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader, User } from 'lucide-react';
import { lookupCoachByCode } from '../utils/coachCodes';
import { colors as theme } from '../theme';

const colors = {
  primary: theme.gold,
  success: theme.success,
  danger: theme.danger,
  bg: theme.bg,
  bgCard: theme.surface1,
  border: theme.borderStrong,
  text: theme.text,
  textMuted: theme.textMuted,
};

/**
 * Coach Code Input Component
 * Validates coach codes and shows coach info
 *
 * Fully self-contained — manages its own text state rather than syncing
 * bidirectionally with a parent-controlled value, since that two-way sync
 * (parent-set value -> local, local -> debounced parent update) caused a
 * runaway render loop that kept re-firing validation indefinitely. The
 * parent doesn't need the raw text anyway — only onCoachValidated matters.
 */
const CoachCodeInput = ({ onCoachValidated, onValidatingChange, hideHelp = false }) => {
  const [localValue, setLocalValue] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationState, setValidationState] = useState(null); // null | 'valid' | 'invalid'
  const [coachInfo, setCoachInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = React.useRef(null);

  // Use refs to avoid including callbacks in the validation effect's deps
  const onCoachValidatedRef = React.useRef(onCoachValidated);
  const onValidatingChangeRef = React.useRef(onValidatingChange);

  useEffect(() => {
    onCoachValidatedRef.current = onCoachValidated;
    onValidatingChangeRef.current = onValidatingChange;
  }, [onCoachValidated, onValidatingChange]);

  // Debounced validation
  useEffect(() => {
    if (!localValue || localValue.length < 3) {
      setValidationState(null);
      setCoachInfo(null);
      setErrorMessage('');
      if (onCoachValidatedRef.current) onCoachValidatedRef.current(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsValidating(true);
      if (onValidatingChangeRef.current) onValidatingChangeRef.current(true);
      setErrorMessage('');

      try {
        const result = await lookupCoachByCode(localValue);

        if (result.success) {
          setValidationState('valid');
          setCoachInfo(result.coach);
          setErrorMessage('');
          if (onCoachValidatedRef.current) onCoachValidatedRef.current(result.coach);
        } else {
          setValidationState('invalid');
          setCoachInfo(null);
          setErrorMessage(result.error || 'Invalid coach code');
          if (onCoachValidatedRef.current) onCoachValidatedRef.current(null);
        }
      } catch (error) {
        console.error('Error validating coach code:', error);
        setValidationState('invalid');
        setErrorMessage('Failed to validate code');
        if (onCoachValidatedRef.current) onCoachValidatedRef.current(null);
      } finally {
        setIsValidating(false);
        if (onValidatingChangeRef.current) onValidatingChangeRef.current(false);
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
  }, [localValue]);

  const getIcon = () => {
    if (isValidating) {
      return <Loader size={20} color={colors.textMuted} className="spin" />;
    }
    if (validationState === 'valid') {
      return <CheckCircle size={20} color={colors.success} />;
    }
    if (validationState === 'invalid') {
      return <XCircle size={20} color={colors.danger} />;
    }
    return null;
  };

  const getBorderColor = () => {
    if (validationState === 'valid') return colors.success;
    if (validationState === 'invalid') return colors.danger;
    return colors.border;
  };

  return (
    <div>
      {/* Input Field */}
      <div style={{ position: 'relative' }}>
        <label
          style={{
            display: 'block',
            fontSize: '13px',
            color: colors.textMuted,
            marginBottom: '8px',
            fontWeight: 500,
          }}
        >
          Coach Code (Optional)
        </label>
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => {
            const newValue = e.target.value.toUpperCase();
            setLocalValue(newValue);
          }}
          placeholder="C-JOHN"
          style={{
            width: '100%',
            padding: '14px 45px 14px 16px',
            fontSize: '15px',
            backgroundColor: colors.bgCard,
            border: `2px solid ${getBorderColor()}`,
            borderRadius: '14px',
            color: colors.text,
            outline: 'none',
            transition: 'all 0.3s ease',
            textTransform: 'uppercase',
            fontWeight: 500,
            letterSpacing: '0.5px',
          }}
          autoComplete="off"
          autoFocus
        />

        {/* Status Icon */}
        <div
          style={{
            position: 'absolute',
            right: '14px',
            top: '38px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getIcon()}
        </div>
      </div>

      {/* Coach Info Card (when valid) */}
      {validationState === 'valid' && coachInfo && (
        <div
          style={{
            marginTop: '12px',
            padding: '16px',
            backgroundColor: `${colors.success}15`,
            border: `2px solid ${colors.success}`,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: colors.success,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <User size={20} color={theme.text} />
          </div>
          <div>
            <div style={{ fontSize: '15px', color: colors.text, fontWeight: 600 }}>
              {coachInfo.name}
            </div>
            <div style={{ fontSize: '13px', color: colors.textMuted }}>
              {coachInfo.role === 'coach' ? 'Coach' : 'Trainer'}
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {validationState === 'invalid' && errorMessage && (
        <div
          style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: `${colors.danger}15`,
            border: `1px solid ${colors.danger}`,
            borderRadius: '8px',
            fontSize: '13px',
            color: colors.danger,
          }}
        >
          {errorMessage}
        </div>
      )}

      {/* Help Text */}
      {!validationState && !hideHelp && (
        <p
          style={{
            fontSize: '13px',
            color: colors.textMuted,
            marginTop: '8px',
            lineHeight: '1.6',
          }}
        >
          If a personal trainer referred you, enter their code here (e.g., C-JOHN). Don't have one? No problem — just skip this step!
        </p>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default CoachCodeInput;
